import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  X, MessageCircle, Mail, Link2, FileDown, CheckCircle2, XCircle,
  ChevronRight, ChevronLeft, Loader2, ShieldCheck, User, Send,
} from "lucide-react";

const STEP_LABELS = ["Documents", "Delivery", "Sharing", "Message", "Preview"];

// Ordered display for the doc catalog.
const KIND_ORDER = [
  "prescription", "invoice", "treatment_plan", "treatment_summary", "payment_receipt",
  "consent", "warranty", "post_op", "medical_history",
  "xray", "clinical_photo", "lab_report", "complete_record",
];

/**
 * Reusable "Share documents" popup — used by any module that wants to send
 * documents to a patient. Open by mounting with `patientId` set; close via
 * `onClose`.
 *
 * Props:
 *   - patientId: string
 *   - preselect?: [{kind, ref_id}]   // pre-check some documents (e.g. from Billing)
 *   - onClose(): void
 *   - onSent?(result): void
 */
export default function ShareDocumentsModal({ patientId, preselect = [], onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [patient, setPatient] = useState(null);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState({});       // key: `${kind}:${id}` -> true
  const [channels, setChannels] = useState({ whatsapp: false, email: false });
  const [mode, setMode] = useState("portal");         // portal | pdf | both
  const [message, setMessage] = useState("");
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);        // set after send

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/patients/${patientId}/shareable-documents`)
      .then(({ data }) => {
        if (cancelled) return;
        setPatient(data.patient);
        setPhone(data.patient.phone || "");
        setEmail(data.patient.email || "");
        setChannels({ whatsapp: !!data.patient.phone, email: !!data.patient.email });
        const ordered = KIND_ORDER
          .map((k) => data.catalog.find((c) => c.kind === k))
          .filter(Boolean);
        setCatalog(ordered);

        if (preselect?.length) {
          const sel = {};
          for (const p of preselect) sel[`${p.kind}:${p.ref_id || p.id || "_"}`] = true;
          setSelected(sel);
        }
      })
      .catch(() => { if (!cancelled) toast.error("Could not load documents"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const selectedDocs = useMemo(() => {
    const out = [];
    for (const c of catalog) {
      for (const item of c.items) {
        const key = `${c.kind}:${item.id}`;
        if (selected[key]) out.push({ kind: c.kind, ref_id: item.id === "_" ? null : item.id, title: item.title });
      }
    }
    return out;
  }, [catalog, selected]);

  const toggle = (kind, id) => {
    const key = `${kind}:${id}`;
    setSelected((s) => ({ ...s, [key]: !s[key] }));
  };

  const anyPdfSelected = selectedDocs.some((d) => {
    const cat = catalog.find((c) => c.kind === d.kind);
    return cat?.pdf_attachable;
  });

  const canProceed = {
    0: selectedDocs.length > 0,
    1: (channels.whatsapp || channels.email) &&
       (!channels.whatsapp || !!phone) &&
       (!channels.email || !!email),
    2: !!mode,
    3: true,
    4: true,
  }[step];

  const submit = async () => {
    setSending(true);
    try {
      const body = {
        patient_id: patientId,
        documents: selectedDocs.map(({ kind, ref_id }) => ({ kind, ref_id })),
        channels: Object.entries(channels).filter(([, v]) => v).map(([k]) => k),
        mode,
        message,
        recipient_phone: phone || null,
        recipient_email: email || null,
        expires_in_days: 30,
      };
      const { data } = await api.post("/document-shares", body);
      setResult(data);
      onSent?.(data);
    } catch (e) {
      toast.error(apiError(e, "Send failed"));
    } finally {
      setSending(false);
    }
  };

  const retry = async () => {
    if (!result?.share?.id) return;
    setSending(true);
    try {
      const { data } = await api.post(`/document-shares/${result.share.id}/retry`);
      setResult((r) => ({ ...r, share: { ...r.share, delivery: data.delivery } }));
      toast.success("Retried failed channels");
    } catch (e) {
      toast.error(apiError(e, "Retry failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-pine/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="share-docs-modal">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E8EAE9] flex items-center justify-between">
          <div>
            <div className="overline text-ink-soft">Share documents</div>
            <div className="font-display text-xl text-ink mt-0.5">
              {result ? "Delivery report" : patient?.name || "…"}
            </div>
          </div>
          <button onClick={onClose} data-testid="share-close" className="p-1 rounded-full hover:bg-linen">
            <X className="w-5 h-5 text-ink-mid" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-ink-mid" /></div>
          ) : result ? (
            <ResultScreen result={result} onRetry={retry} sending={sending} onClose={onClose} />
          ) : (
            <>
              {/* Patient contacts */}
              <div className="px-6 pt-5 pb-4 bg-linen/50 border-b border-[#E8EAE9]">
                <div className="flex items-center gap-2 text-xs text-ink-mid mb-2">
                  <User className="w-3.5 h-3.5" /> Recipient
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    data-testid="share-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="WhatsApp / phone"
                    className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pine/40" />
                  <input
                    data-testid="share-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pine/40" />
                </div>
              </div>

              {/* Stepper */}
              <div className="px-6 pt-4">
                <div className="flex items-center gap-1 mb-4 text-xs">
                  {STEP_LABELS.map((label, i) => (
                    <div key={label} className={`flex items-center gap-1 ${i <= step ? "text-ink" : "text-ink-disabled"}`}>
                      <div className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-semibold ${i <= step ? "bg-pine text-linen" : "bg-linen text-ink-soft"}`}>{i + 1}</div>
                      <span className="hidden sm:inline">{label}</span>
                      {i < STEP_LABELS.length - 1 && <span className="text-ink-disabled mx-1">·</span>}
                    </div>
                  ))}
                </div>

                {/* Step content */}
                {step === 0 && <StepDocuments catalog={catalog} selected={selected} toggle={toggle} />}
                {step === 1 && (
                  <StepDelivery channels={channels} setChannels={setChannels} phone={phone} email={email} />
                )}
                {step === 2 && (
                  <StepSharing mode={mode} setMode={setMode} anyPdfSelected={anyPdfSelected} />
                )}
                {step === 3 && (
                  <StepMessage message={message} setMessage={setMessage} />
                )}
                {step === 4 && (
                  <StepPreview
                    selectedDocs={selectedDocs} channels={channels} mode={mode}
                    phone={phone} email={email} message={message} />
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!result && !loading && (
          <div className="border-t border-[#E8EAE9] px-6 py-3 flex items-center justify-between bg-white">
            <div className="text-xs text-ink-soft">
              {selectedDocs.length} doc{selectedDocs.length === 1 ? "" : "s"} · {[
                channels.whatsapp && "WhatsApp",
                channels.email && "Email",
              ].filter(Boolean).join(" + ") || "no channel"}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button data-testid="share-back" onClick={() => setStep(step - 1)}
                        className="btn-outline text-sm inline-flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
              )}
              {step < STEP_LABELS.length - 1 ? (
                <button data-testid="share-next"
                        disabled={!canProceed}
                        onClick={() => setStep(step + 1)}
                        className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button data-testid="share-send"
                        disabled={sending || !canProceed || selectedDocs.length === 0}
                        onClick={submit}
                        className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Steps ---------- */

function StepDocuments({ catalog, selected, toggle }) {
  if (catalog.length === 0) {
    return <div className="py-8 text-center text-sm text-ink-soft">This patient has no shareable documents yet.</div>;
  }
  return (
    <div className="space-y-4 pb-4">
      {catalog.map((c) => (
        <div key={c.kind} data-testid={`share-cat-${c.kind}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-xs font-medium text-ink uppercase tracking-wider">{c.label}</div>
            {!c.pdf_attachable && (
              <span className="text-[10px] bg-linen text-ink-mid px-1.5 py-0.5 rounded-full">portal only</span>
            )}
            <span className="text-[10px] text-ink-soft">· {c.count}</span>
          </div>
          <div className="space-y-1">
            {c.items.map((item) => {
              const key = `${c.kind}:${item.id}`;
              return (
                <label key={key} data-testid={`share-doc-${key}`}
                       className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition ${selected[key] ? "bg-pine/5 border border-pine/20" : "hover:bg-linen border border-transparent"}`}>
                  <input
                    type="checkbox"
                    checked={!!selected[key]}
                    onChange={() => toggle(c.kind, item.id)}
                    className="w-4 h-4 accent-pine" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">{item.title}</div>
                    {item.subtitle && <div className="text-xs text-ink-soft truncate">{item.subtitle}</div>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function StepDelivery({ channels, setChannels, phone, email }) {
  const Row = ({ id, icon: Icon, title, desc, disabled, checked, onChange }) => (
    <label
      data-testid={`share-channel-${id}`}
      className={`flex items-start gap-3 p-4 rounded-xl border transition cursor-pointer ${
        disabled ? "border-[#E8EAE9] bg-linen/30 opacity-50 cursor-not-allowed"
                 : checked ? "border-pine bg-pine/5" : "border-[#E8EAE9] hover:border-pine/40"
      }`}>
      <input type="checkbox" disabled={disabled} checked={checked} onChange={onChange} className="mt-0.5 w-4 h-4 accent-pine" />
      <div className="flex-1">
        <div className="flex items-center gap-2 text-ink text-sm font-medium">
          <Icon className="w-4 h-4" /> {title}
        </div>
        <div className="text-xs text-ink-mid mt-0.5">{desc}</div>
      </div>
    </label>
  );
  return (
    <div className="space-y-2 pb-4">
      <Row id="whatsapp" icon={MessageCircle}
           title="WhatsApp"
           desc={phone ? `To ${phone}` : "No phone on file"}
           disabled={!phone}
           checked={channels.whatsapp}
           onChange={(e) => setChannels((c) => ({ ...c, whatsapp: e.target.checked }))} />
      <Row id="email" icon={Mail}
           title="Email"
           desc={email ? `To ${email}` : "No email on file"}
           disabled={!email}
           checked={channels.email}
           onChange={(e) => setChannels((c) => ({ ...c, email: e.target.checked }))} />
    </div>
  );
}

function StepSharing({ mode, setMode, anyPdfSelected }) {
  const Card = ({ id, icon: Icon, title, desc, benefits, recommended, disabled }) => (
    <label
      data-testid={`share-mode-${id}`}
      className={`block p-4 rounded-xl border transition cursor-pointer relative ${
        disabled ? "opacity-40 cursor-not-allowed border-[#E8EAE9]"
                 : mode === id ? "border-pine bg-pine/5" : "border-[#E8EAE9] hover:border-pine/40"
      }`}>
      <input type="radio" name="share-mode" disabled={disabled} checked={mode === id}
             onChange={() => setMode(id)} className="sr-only" />
      {recommended && (
        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-pine text-linen px-2 py-0.5 rounded-full">Recommended</span>
      )}
      <div className="flex items-center gap-2 text-ink font-medium">
        <Icon className="w-4 h-4" /> {title}
      </div>
      <div className="text-xs text-ink-mid mt-1 mb-2">{desc}</div>
      {benefits && (
        <ul className="text-[11px] text-ink-soft space-y-0.5">
          {benefits.map((b) => <li key={b}>• {b}</li>)}
        </ul>
      )}
    </label>
  );
  return (
    <div className="space-y-2 pb-4">
      <Card id="portal" icon={Link2} title="Secure patient portal" recommended
            desc="Patient receives a private link showing only the selected documents."
            benefits={["Always latest version", "Mobile-friendly", "Revocable", "View history logged"]} />
      <Card id="pdf" icon={FileDown} title="PDF attachments"
            disabled={!anyPdfSelected}
            desc={anyPdfSelected ? "Generate PDFs and send directly via WhatsApp/Email." : "None of the selected docs have a PDF version yet."} />
      <Card id="both" icon={ShieldCheck} title="Portal link + PDF attachments"
            disabled={!anyPdfSelected}
            desc="Send both — patient gets attachments and a live portal link." />
    </div>
  );
}

function StepMessage({ message, setMessage }) {
  return (
    <div className="pb-4">
      <div className="text-xs text-ink-mid mb-2">Optional custom message (patient sees this in both WhatsApp and email)</div>
      <textarea
        data-testid="share-message"
        rows={5}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Dear Rahul,&#10;Please find your prescription and post-operative instructions.&#10;Wishing you a speedy recovery.&#10;— Smile Dental Clinic"
        className="w-full rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-pine/40" />
    </div>
  );
}

function StepPreview({ selectedDocs, channels, mode, phone, email, message }) {
  const modeLabel = { portal: "Secure Portal", pdf: "PDF Attachments", both: "Portal + PDF" }[mode];
  return (
    <div className="pb-4 text-sm">
      <PreviewSection title="Documents">
        <ul className="space-y-1">
          {selectedDocs.map((d, i) => <li key={i} className="text-ink">• {d.title}</li>)}
        </ul>
      </PreviewSection>
      <PreviewSection title="Delivery">
        <div className="flex flex-wrap gap-2">
          {channels.whatsapp && <Chip icon={MessageCircle}>{`WhatsApp · ${phone}`}</Chip>}
          {channels.email && <Chip icon={Mail}>{`Email · ${email}`}</Chip>}
        </div>
      </PreviewSection>
      <PreviewSection title="Sharing method">
        <Chip icon={mode === "pdf" ? FileDown : Link2}>{modeLabel}</Chip>
      </PreviewSection>
      {message && (
        <PreviewSection title="Message">
          <div className="whitespace-pre-wrap text-ink bg-linen/50 rounded-lg p-3 text-xs">{message}</div>
        </PreviewSection>
      )}
    </div>
  );
}

function PreviewSection({ title, children }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-wider text-ink-soft mb-1.5">{title}</div>
      {children}
    </div>
  );
}
function Chip({ icon: Icon, children }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-linen text-ink text-xs px-2 py-1 rounded-full">
      <Icon className="w-3.5 h-3.5" /> {children}
    </div>
  );
}

/* ---------- Result screen ---------- */
function ResultScreen({ result, onRetry, sending, onClose }) {
  const delivery = result?.share?.delivery || {};
  const wa = delivery.whatsapp;
  const em = delivery.email;
  const anyFailed = [wa, em].filter(Boolean).some((d) => d.status === "failed");
  const portalUrl = result?.share?.token
    ? `${window.location.origin}/share/${result.share.token}` : null;
  return (
    <div className="p-6 space-y-4">
      <div className="text-xs text-ink-soft">Delivery status</div>
      <div className="space-y-2">
        {wa && <StatusRow icon={MessageCircle} label="WhatsApp" status={wa.status} detail={wa.details?.length ? `${wa.details.length} message(s)` : ""} />}
        {em && <StatusRow icon={Mail} label="Email" status={em.status} detail={em.id ? `id ${em.id.substring(0,8)}` : em.error} />}
      </div>
      {portalUrl && (
        <div className="mt-4">
          <div className="text-xs text-ink-soft mb-1">Portal link (copy to share manually)</div>
          <div className="flex items-center gap-2 bg-linen rounded-lg px-3 py-2 text-xs text-ink break-all">
            <Link2 className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1 min-w-0 truncate">{portalUrl}</span>
            <button
              data-testid="share-copy-link"
              className="text-clay hover:underline shrink-0"
              onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Copied"); }}>
              Copy
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 pt-2">
        {anyFailed && (
          <button data-testid="share-retry" onClick={onRetry} disabled={sending}
                  className="btn-outline text-sm inline-flex items-center gap-1">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Retry failed
          </button>
        )}
        <button data-testid="share-done" onClick={onClose} className="btn-primary text-sm ml-auto">Done</button>
      </div>
    </div>
  );
}

function StatusRow({ icon: Icon, label, status, detail }) {
  const SUCCESS = ["sent", "success", "submitted", "queued", "delivered"];
  const isOk = SUCCESS.includes(status);
  const isMocked = status === "mocked";
  const Emoji = isOk ? CheckCircle2 : isMocked ? CheckCircle2 : XCircle;
  const cls = isOk ? "text-[#2D6A4F]" : isMocked ? "text-ink-mid" : "text-clay";
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-[#E8EAE9]">
      <Icon className="w-4 h-4 text-ink-mid" />
      <div className="flex-1">
        <div className="text-sm text-ink">{label}</div>
        {detail && <div className="text-[11px] text-ink-soft">{detail}</div>}
      </div>
      <div className={`inline-flex items-center gap-1 text-xs ${cls}`}>
        <Emoji className="w-4 h-4" />
        {isOk ? "Sent" : isMocked ? "Mocked" : "Failed"}
      </div>
    </div>
  );
}
